import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot } from '@angular/router'
import { UserGroup, UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { ErrorPageService, unwrapData } from '@ftr/foundation'
import { CourtSystemParams } from '@ftr/routing-paths'
import { Observable, map, tap } from 'rxjs'
import { UserPermissionService } from '../../services'

/**
 * UserPermissionsGuard checks that a user has a specified permission in a court
 * system before allowing access to the requested route.
 *
 * To use this guard, the following preconditions must be met:
 *
 * - The court system id must be present in the route
 * - A `requiresPermission` property is set in the route's data
 *
 * For example, to guard `/court-system/:id/admin/repositories/add` by only
 * allowing users with `WriteRecordingVault` permission, we can declare the
 * route as:
 *
 * ```typescript
 * {
 *   path: 'add',
 *   component: RepositoryAddPage,
 *   data: {
 *     breadcrumb: 'Add',
 *     title: 'Add Repository',
 *     requiresPermission: UserGroupPermissionId.WriteRecordingVault
 *   },
 *   canActivate: [UserPermissionGuard]
 * },
 * ```
 *
 * To apply the guard to child routes, use `canActivateChild` [1] instead of
 * `canActivate` [2]. For more information, please see Angular documentation on
 * how to use guards [3].
 *
 * [1]: https://angular.io/api/router/CanActivateChild
 * [2]: https://angular.io/api/router/CanActivate
 * [3]: https://angular.io/guide/router-tutorial-toh#milestone-5-route-guards
 */
@Injectable()
export class UserPermissionGuard {
  constructor(
    private readonly userPermissionService: UserPermissionService,
    private readonly errorPageService: ErrorPageService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    this.validateRouteData(route)

    const courtSystemId = route.params[CourtSystemParams.CourtSystemId]

    return this.userPermissionService.fetchCurrentUserGroupsForCourtSystem(courtSystemId).pipe(
      unwrapData(),
      map(userGroups => {
        const enabledGroups = userGroups.filter(userGroup => userGroup.isEnabled)
        const currentUserPermissions = mapUserGroupsToPermissionsSet(enabledGroups)
        return currentUserPermissions.has(route.data.requiresPermission)
      }),
      tap(hasPermission => {
        if (!hasPermission) {
          this.errorPageService.showForbiddenPage()
        }
      }),
    )
  }

  canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.canActivate(route)
  }

  private validateRouteData(route: ActivatedRouteSnapshot): void {
    const requiredProperties = ['requiresPermission']
    requiredProperties.forEach(property => {
      if (route.data[property] === undefined) {
        throw new Error(`Missing UserPermissionGuard data property '${property}'`)
      }
    })
  }
}

/**
 * Given an array of user groups, return a set of permissions that those groups
 * represent
 */
export function mapUserGroupsToPermissionsSet(userGroups: UserGroup[]): Set<UserGroupPermissionId> {
  const userGroupPermissions = userGroups.map(userGroup => userGroup.permissions.map(permission => permission.id))
  const mergedPermissions = new Array<UserGroupPermissionId>().concat(...userGroupPermissions)
  return new Set(mergedPermissions)
}
